import Visualization from '../visualization';
import Transformation from '../../tabledata/transformation';
const SETTING_TEMPLATE = require('./visualization-heatmap-setting.html');

const BLUE_RAMP = ['#eff3ff', '#bdd7e7', '#6baed6', '#3182bd', '#08519c'];

const AGGR_FNS = {
  sum: function(vs) {
    return vs.reduce(function(a, b) {
      return a + b;
    }, 0);
  },
  mean: function(vs) {
    return vs.reduce(function(a, b) {
      return a + b;
    }, 0) / vs.length;
  },
  min: function(vs) {
    return Math.min.apply(null, vs);
  },
  max: function(vs) {
    return Math.max.apply(null, vs);
  },
};

// ─── Transformation ──────────────────────────────────────────────────────────

class HeatmapTransformation extends Transformation {
  constructor(config) {
    super(config);
    if (!config.aggr) {
      config.aggr = 'sum';
    }
  }

  getSetting() {
    return {
      template: SETTING_TEMPLATE,
      scope: {
        config: this.config,
        tableDataColumns: this.tableDataColumns,
        save: () => this.emitConfig(this.config),
      },
    };
  }

  transform(tableData) {
    this.tableDataColumns = tableData.columns;
    this._removeUnknown(tableData.columns);
    return tableData;
  }

  _removeUnknown(columns) {
    ['xAxis', 'yAxis', 'metric'].forEach((field) => {
      const v = this.config[field];
      if (v && !columns.find((c) => c.index === v.index && c.name === v.name)) {
        this.config[field] = null;
      }
    });
  }
}

// ─── Visualization ───────────────────────────────────────────────────────────

export default class HeatmapVisualization extends Visualization {
  constructor(targetEl, config) {
    super(targetEl, config);
    this.transformation = new HeatmapTransformation(config);
    this._tableData = null;
  }

  type() {
    return 'heatmap';
  }

  getTransformation() {
    return this.transformation;
  }

  refresh() {
    if (this._tableData) {
      this._draw(this._tableData);
    }
  }

  render(tableData) {
    this._tableData = tableData;
    this._selectDefault(tableData.columns);
    this._draw(tableData);
  }

  destroy() {
    this.targetEl.empty();
  }

  _selectDefault(columns) {
    if (!this.config.xAxis && !this.config.yAxis && !this.config.metric && columns.length >= 3) {
      this.config.xAxis = columns[0];
      this.config.yAxis = columns[1];
      this.config.metric = columns[2];
    }
  }

  _draw(tableData) {
    const cfg = this.config;
    this.targetEl.empty();

    if (!cfg.xAxis || !cfg.yAxis || !cfg.metric) {
      this.targetEl.html(
        '<div style="padding:24px;color:#999;text-align:center;font-size:13px;">' +
        'Open the settings panel and select X Axis, Y Axis, and Metric.</div>'
      );
      return;
    }

    // ── Resolve column indices ────────────────────────────────────────────────
    const cols = tableData.columns;
    const xi = cols.findIndex((c) => c.index === cfg.xAxis.index);
    const yi = cols.findIndex((c) => c.index === cfg.yAxis.index);
    const mi = cols.findIndex((c) => c.index === cfg.metric.index);
    if (xi < 0 || yi < 0 || mi < 0) {
      return;
    }

    // ── Group rows and aggregate ──────────────────────────────────────────────
    const groups = {};
    for (let i = 0; i < tableData.rows.length; i++) {
      const row = tableData.rows[i];
      const xv = String(row[xi]);
      const yv = String(row[yi]);
      if (!groups[xv]) {
        groups[xv] = {};
      }
      if (!groups[xv][yv]) {
        groups[xv][yv] = [];
      }
      groups[xv][yv].push(parseFloat(row[mi]) || 0);
    }

    const aggrFn = AGGR_FNS[cfg.aggr] || AGGR_FNS.sum;
    const cells = [];
    const ySet = {};

    Object.keys(groups).forEach((xv) => {
      Object.keys(groups[xv]).forEach((yv) => {
        ySet[yv] = true;
        cells.push({x: xv, y: yv, v: aggrFn(groups[xv][yv])});
      });
    });

    if (cells.length === 0) {
      return;
    }

    const xLabels = Object.keys(groups).sort();
    const yLabels = Object.keys(ySet).sort();
    const values = cells.map(function(c) {
      return c.v;
    });
    const minV = d3.min(values);
    const maxV = d3.max(values);
    const colorDomain = (minV === maxV) ? [minV - 1, maxV + 1] : [minV, maxV];

    // ── Layout ────────────────────────────────────────────────────────────────
    const margin = {top: 20, right: 30, bottom: 90, left: 100};
    // Enforce a minimum height so axis labels and cell text are readable.
    // config.graph.height defaults to 300 which is too small; bump it here.
    const minHeight = 480;
    if ((this.targetEl.height() || 0) < minHeight) {
      this.targetEl.height(minHeight);
    }
    const totalW = this.targetEl.width() || 640;
    const totalH = this.targetEl.height();
    const w = Math.max(totalW - margin.left - margin.right, 100);
    const h = Math.max(totalH - margin.top - margin.bottom, 100);

    const svg = d3.select(this.targetEl[0])
      .append('svg')
      .attr('width', w + margin.left + margin.right)
      .attr('height', h + margin.top + margin.bottom)
      .append('g')
      .attr('transform', 'translate(' + margin.left + ',' + margin.top + ')');

    // ── Scales ────────────────────────────────────────────────────────────────
    const xScale = d3.scale.ordinal().domain(xLabels).rangeBands([0, w], 0.05);
    const yScale = d3.scale.ordinal().domain(yLabels).rangeBands([0, h], 0.05);
    const colorScale = d3.scale.quantile().domain(colorDomain).range(BLUE_RAMP);

    // ── Axes ──────────────────────────────────────────────────────────────────
    svg.append('g')
      .attr('class', 'x axis')
      .attr('transform', 'translate(0,' + h + ')')
      .call(d3.svg.axis().scale(xScale).orient('bottom'))
      .selectAll('text')
      .style('fill', '#333')
      .style('font-size', '13px')
      .style('text-anchor', 'end')
      .attr('dx', '-0.8em')
      .attr('dy', '0.15em')
      .attr('transform', 'rotate(-40)');

    svg.append('g')
      .attr('class', 'y axis')
      .call(d3.svg.axis().scale(yScale).orient('left'))
      .selectAll('text')
      .style('fill', '#333')
      .style('font-size', '13px');

    // ── Tooltip ───────────────────────────────────────────────────────────────
    const tooltip = d3.select(this.targetEl[0])
      .append('div')
      .style('position', 'absolute')
      .style('background', 'rgba(0,0,0,0.75)')
      .style('color', '#fff')
      .style('padding', '4px 8px')
      .style('border-radius', '4px')
      .style('font-size', '12px')
      .style('pointer-events', 'none')
      .style('opacity', 0);

    const cellW = xScale.rangeBand();
    const cellH = yScale.rangeBand();

    // ── Cells ─────────────────────────────────────────────────────────────────
    svg.selectAll('.hm-cell')
      .data(cells)
      .enter()
      .append('rect')
      .attr('class', 'hm-cell')
      .attr('x', function(d) {
        return xScale(d.x);
      })
      .attr('y', function(d) {
        return yScale(d.y);
      })
      .attr('width', cellW)
      .attr('height', cellH)
      .style('fill', function(d) {
        return colorScale(d.v);
      })
      .style('stroke', '#fff')
      .style('stroke-width', '1px')
      .on('mouseover', function(d) {
        tooltip.transition().duration(100).style('opacity', 1);
        tooltip.html('<b>' + d.x + '</b> × <b>' + d.y + '</b><br/>' + d3.round(d.v, 4));
        d3.select(this).style('stroke', '#333').style('stroke-width', '2px');
      })
      .on('mousemove', function() {
        let pos = d3.mouse(svg.node());
        tooltip
          .style('left', (pos[0] + margin.left + 10) + 'px')
          .style('top', (pos[1] + margin.top - 10) + 'px');
      })
      .on('mouseout', function() {
        tooltip.transition().duration(100).style('opacity', 0);
        d3.select(this).style('stroke', '#fff').style('stroke-width', '1px');
      });

    // ── Value labels (only when cells are large enough) ───────────────────────
    if (cellW > 36 && cellH > 18) {
      // Use colorScale's quantile thresholds to pick contrasting text.
      // The last two BLUE_RAMP shades (#3182bd, #08519c) are dark → white text.
      const darkThreshold = colorScale.quantiles()[BLUE_RAMP.length - 3];
      svg.selectAll('.hm-label')
        .data(cells)
        .enter()
        .append('text')
        .attr('class', 'hm-label')
        .attr('x', function(d) {
          return xScale(d.x) + cellW / 2;
        })
        .attr('y', function(d) {
          return yScale(d.y) + cellH / 2;
        })
        .attr('dy', '0.35em')
        .attr('text-anchor', 'middle')
        .style('font-size', '11px')
        .style('font-weight', '500')
        .style('fill', function(d) {
          return d.v >= darkThreshold ? '#fff' : '#222';
        })
        .text(function(d) {
          return d3.format('.2s')(d.v);
        });
    }

    // ── Color legend ──────────────────────────────────────────────────────────
    const legendW = Math.min(200, Math.floor(w * 0.6));
    const legendX = Math.floor((w - legendW) / 2);
    const legendY = h + margin.bottom - 20;
    const segW = Math.floor(legendW / BLUE_RAMP.length);

    const legendG = svg.append('g')
      .attr('transform', 'translate(' + legendX + ',' + legendY + ')');

    BLUE_RAMP.forEach(function(color, i) {
      legendG.append('rect')
        .attr('x', i * segW).attr('y', 0)
        .attr('width', segW).attr('height', 8)
        .style('fill', color);
    });

    const legendScale = d3.scale.linear().domain([minV, maxV]).range([0, legendW]);
    legendG.append('g')
      .attr('transform', 'translate(0,8)')
      .call(d3.svg.axis().scale(legendScale).orient('bottom').ticks(4).tickFormat(d3.format('.2s')))
      .selectAll('text')
      .style('fill', '#333')
      .style('font-size', '11px');
    legendG.select('.domain').style('display', 'none');
  }
}
